import { useCallback, useMemo } from "react";
import { AuthContext, useAuth } from "./AuthContext";

export const PLATFORM_YOUTUBE = 'youtube' as const;
export const PLATFORM_VK = 'vk' as const;
export type Platform = typeof PLATFORM_YOUTUBE | typeof PLATFORM_VK;

export type Playlist = { platform: Platform; playlist_id: string; title: string; subject_id?: number | null };

export function playlistUrl(platform: Platform, channelId: string, playlistId: string): string {
  if (platform === PLATFORM_YOUTUBE) return `https://www.youtube.com/playlist?list=${playlistId}`;
  if (platform === PLATFORM_VK) return `https://vkvideo.ru/playlist/-${channelId}_${playlistId}`;
  return "";
}

/** Splits the "<status> <text>: <body>" error thrown by AuthMiddleware into status and FastAPI `detail`. */
export function parseApiError(e: any): { status: number; detail: any; message: string } {
  const msg: string = e?.message ?? String(e);
  const m = msg.match(/^(\d{3}) [^:]*: ([\s\S]*)$/);
  if (!m) return { status: 0, detail: null, message: msg };
  let detail: any = m[2];
  try { detail = JSON.parse(m[2]).detail ?? detail; } catch { /* plain text body */ }
  const message = typeof detail === 'string' ? detail : detail?.message ?? (Array.isArray(detail) ? detail.map((d: any) => d.msg).join('; ') : msg);
  return { status: Number(m[1]), detail, message };
}
export type Channel = { id: string; description: string; playlists: Playlist[] };

export const TASK_STATE_PENDING = 'pending' as const;
export const TASK_STATE_RUNNING = 'running' as const;
export const TASK_STATE_COMPLETED = 'completed' as const;
export const TASK_STATE_FAILED = 'failed' as const;
export type TaskState = typeof TASK_STATE_PENDING | typeof TASK_STATE_RUNNING | typeof TASK_STATE_COMPLETED | typeof TASK_STATE_FAILED;

export interface UploadTask {
  id: string; state: TaskState; created_by: string; created_at: Date;
  source_share: string; source_path: string; source_thumbnail_path?: string;
  target_platform: Platform; target_title: string; target_description: string;
  target_channel_ref: string; target_playlist_ref: string | null; target_public?: boolean;
  result_id?: string; progress?: number; lecture_id?: number | null;
  subject_title?: string | null; lecture_number?: number | null; lecture_subject_id?: number | null;
}

export interface UploadTaskEvent { id: string; task_id: string; timestamp: Date; body: string }
export interface CreateUploadTaskReq {
  path: string; thumbnail_path: string; platform: Platform; title: string;
  description: string; channel_ref: string; playlist_ref: string | null; public?: boolean; lecture_id?: number;
}

export interface VkCheckLoginResult { user_id: number; device_id: string; scope: string; expires_at: number }
export interface YoutubeCheckLoginResult { scopes: string[]; expiry: Date }
export interface StartLoginResult { redirect_uri: string }
export type Fetch = (url: string | URL, init?: RequestInit) => Promise<any>;

class AuthMiddleware {
  constructor(private readonly auth: AuthContext) {}
  async fetch(url: string | URL, init?: RequestInit) {
    const { token } = await this.auth.identity();
    const disabledRoles = this.auth.getDisabledRoles();
    const rsp = await fetch(url, {
      ...init,
      headers: {
        'authorization': `Bearer ${token}`,
        ...(disabledRoles.length > 0 ? { 'x-debug-disabled-roles': disabledRoles.join(',') } : {}),
        ...init?.headers,
      },
    });
    if (!rsp.ok) throw new Error(`${rsp.status} ${rsp.statusText}: ${await rsp.text()}`);
    if (rsp.status === 204) return null;
    const text = await rsp.text();
    return text ? JSON.parse(text) : null;
  }
}

export function useFetch() {
  const auth = useAuth();
  const middleware = useMemo(() => new AuthMiddleware(auth), [auth]);
  return { fetch: useCallback<typeof middleware.fetch>((...args) => middleware.fetch(...args), [middleware]) };
}

const json = (method: string, body?: unknown): RequestInit => ({
  method,
  ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  headers: { 'content-type': 'application/json' },
});

export const getChannels = (o: { fetch: Fetch }): Promise<{ vk: Channel[]; youtube: Channel[] }> => o.fetch('/api/playlists');

export interface BrowseEntry { name: string; type: 'file' | 'dir' }
export interface BrowseResult { path: string; entries: BrowseEntry[] }
export const browse = (o: { path: string; mode?: 'video' | 'image' | 'all'; fetch: Fetch }): Promise<BrowseResult> =>
  o.fetch(`/api/browse?path=${encodeURIComponent(o.path)}&mode=${o.mode ?? 'all'}`);

export async function getTasks(o: { limit: number; start: number; state?: TaskState; mine?: boolean; createdBy?: string; fetch: Fetch }): Promise<UploadTask[]> {
  const p = new URLSearchParams({ limit: String(o.limit), start: String(o.start) });
  if (o.state) p.set('state', o.state);
  if (o.mine) p.set('mine', 'true');
  else if (o.createdBy) p.set('created_by', o.createdBy);
  const data = await o.fetch(`/api/tasks?${p}`);
  return data.map((t: any) => ({ ...t, created_at: new Date(t.created_at) }));
}

/** A render started from the editor; its state is kept up to date from Celery by the server. */
export interface RenderJob { id: string; created_by: string; created_at: string; finished_at: string | null; state: TaskState | 'cancelled'; output_path: string; error: string | null; percent: number | null }
export function getRenderJobs(o: { limit: number; start: number; state?: string; mine?: boolean; createdBy?: string; fetch: Fetch }): Promise<RenderJob[]> {
  const p = new URLSearchParams({ limit: String(o.limit), start: String(o.start) });
  if (o.state) p.set('state', o.state);
  if (o.mine) p.set('mine', 'true');
  else if (o.createdBy) p.set('created_by', o.createdBy);
  return o.fetch(`/api/render_jobs?${p}`);
}

export async function getTask(o: { task_id: string; fetch: Fetch }): Promise<UploadTask> {
  const data = await o.fetch(`/api/tasks/${o.task_id}`);
  return { ...data, created_at: new Date(data.created_at) };
}

export async function getTaskEvents(o: { task_id: string; limit: number; fetch: Fetch }): Promise<UploadTaskEvent[]> {
  const data = await o.fetch(`/api/tasks/${o.task_id}/events?limit=${o.limit}`);
  return data.map((e: any) => ({ ...e, timestamp: new Date(e.timestamp) }));
}

export const createTask = (o: { task: CreateUploadTaskReq; fetch: Fetch }): Promise<UploadTask> =>
  o.fetch('/api/tasks', json('POST', o.task));

export const checkLoginVk = (o: { fetch: Fetch }): Promise<VkCheckLoginResult> => o.fetch('/api/vk/status', { method: 'POST' });
export const startLoginVk = (o: { fetch: Fetch }): Promise<StartLoginResult> => o.fetch('/api/vk/login', { method: 'POST' });
export const syncPlaylistsVk = (o: { channelId: string; fetch: Fetch }): Promise<{}> =>
  o.fetch(`/api/playlists/vk/channels/${encodeURIComponent(o.channelId)}/sync`, { method: 'POST' });

export async function checkLoginYoutube(o: { fetch: Fetch }): Promise<YoutubeCheckLoginResult> {
  const data = await o.fetch('/api/youtube/status', { method: 'POST' });
  return { ...data, expiry: new Date(data.expiry * 1000) };
}

export const startLoginYoutube = (o: { fetch: Fetch }): Promise<StartLoginResult> => o.fetch('/api/youtube/login', { method: 'POST' });
export const syncPlaylistsYoutube = (o: { channelId: string; fetch: Fetch }): Promise<{}> =>
  o.fetch(`/api/playlists/youtube/channels/${encodeURIComponent(o.channelId)}/sync`, { method: 'POST' });

export interface UiConfig { bug_report_url: string | null; allow_role_override: boolean }
export async function getUiConfig(): Promise<UiConfig> {
  const rsp = await fetch('/api/ui-config');
  if (!rsp.ok) throw new Error(`${rsp.status} ${rsp.statusText}`);
  return rsp.json();
}

// Admin domain
export interface Semester { id: string; title: string; starts_on: string | null; ends_on: string | null; is_active: boolean }
export interface SemesterUpsert { id: string; title?: string; starts_on?: string | null; ends_on?: string | null; is_active?: boolean }
export interface Subject {
  id: number; semester_id: string | null; title: string; awh_multiplier: number;
  operator_money_coef: number; editor_money_coef: number;
  reviewer_archived?: boolean; editor_archived?: boolean;
  reviewer_id: string | null; editor_id: string | null; reviewer_name?: string | null; editor_name?: string | null;
  playlists?: SubjectPlaylist[];
}
export interface SubjectPlaylist { platform: Platform; channel_id: string; playlist_id: string }
export type AttachPlaylistReq = { channel_id: string } & ({ playlist_id: string } | { title: string; public?: boolean; force?: boolean });
export const attachSubjectPlaylist = (o: { subjectId: number; platform: Platform; req: AttachPlaylistReq; fetch: Fetch }): Promise<SubjectPlaylist> =>
  o.fetch(`/api/subjects/${o.subjectId}/playlists/${o.platform}`, json('POST', o.req));
export const detachSubjectPlaylist = (o: { subjectId: number; platform: Platform; fetch: Fetch }): Promise<null> =>
  o.fetch(`/api/subjects/${o.subjectId}/playlists/${o.platform}`, { method: 'DELETE' });
export const updatePlaylist = (o: { platform: Platform; channelId: string; playlistId: string; patch: { title?: string; subject_id?: number | null }; fetch: Fetch }): Promise<null> =>
  o.fetch(`/api/playlists/${o.platform}/channels/${encodeURIComponent(o.channelId)}/${encodeURIComponent(o.playlistId)}`, json('PATCH', o.patch));
export interface SubjectInput { semester_id?: string | null; title?: string; awh_multiplier?: number; operator_money_coef?: number; editor_money_coef?: number; reviewer_id?: string | null; editor_id?: string | null }

export const REVIEW_STATES = ['pending', 'rework', 'approved'] as const;
export type ReviewState = typeof REVIEW_STATES[number];

export interface ReviewPublication { platform: Platform; video_id: string; channel_ref: string | null }
export interface ReviewLecture { id: number; number: number; title: string | null; review_state: ReviewState; publications: ReviewPublication[] }
export interface ReviewSubject { id: number; title: string; semester_id: string | null; lectures: ReviewLecture[] }
/** 'admin' lists every subject, for the admin panel. */
export type ReviewRole = 'editor' | 'reviewer' | 'admin';

export function getVideoUrl(platform: Platform, videoId: string, channelRef: string): string {
  if (platform === PLATFORM_YOUTUBE) return `https://www.youtube.com/watch?v=${videoId}`;
  if (platform === PLATFORM_VK) return `https://vk.com/video-${channelRef}_${videoId}`;
  return '';
}

export const getReviewSubjects = (o: { role: ReviewRole; semesterId?: string | null; fetch: Fetch }): Promise<ReviewSubject[]> => {
  const p = new URLSearchParams({ role: o.role });
  if (o.semesterId) p.set('semester_id', o.semesterId);
  return o.fetch(`/api/review/subjects?${p}`);
};

export const setLectureReviewState = (o: { lectureId: number; reviewState: ReviewState; fetch: Fetch }): Promise<ReviewLecture> =>
  o.fetch(`/api/review/lectures/${o.lectureId}`, json('PATCH', { review_state: o.reviewState }));

export const AWH_ROLES = ['operator', 'editor'] as const;
export type AwhRole = typeof AWH_ROLES[number];
export const AWH_STATES = ['pending', 'blocked', 'success', 'failed'] as const;
export type AwhState = typeof AWH_STATES[number];

export interface AwhSubmission {
  id: number; lecture_id: number | null; lecture_number: number | null; subject_id: number | null; role: AwhRole;
  student_id: string | null; student_name: string | null; last_name: string | null; first_name: string | null; student_archived?: boolean;
  awh: number; money_coef: number; state: AwhState; created_at: string; success_at: string | null; subject_title: string | null;
  created_by?: string | null; created_via?: 'lecture' | 'manual' | 'legacy' | null; edited_by?: string | null; edited_at?: string | null;
}
export interface AwhSubmissionCreate { role: AwhRole; awh: number; money_coef?: number; subject_id?: number | null; state: AwhState; student_id?: string | null; student_name?: string | null; lecture_id?: number | null }

export const REGISTRATION_STATUSES = ['pending', 'approved', 'declined'] as const;
export type RegistrationStatus = typeof REGISTRATION_STATUSES[number];

export interface Registration {
  id: string; status: RegistrationStatus; phystech_email: string; last_name: string; first_name: string;
  middle_name: string | null; gmail: string | null; vk_id: string | null; mipt_tech_tag: string | null;
  created_at: string; reviewed_by: string | null; reviewed_at: string | null; decline_reason: string | null;
}
export interface RegistrationUpdate {
  last_name?: string; first_name?: string; middle_name?: string | null; phystech_email?: string;
  gmail?: string | null; vk_id?: string | null; mipt_tech_tag?: string | null;
}

export interface Template { id: string; owner_id: string; subject_id: number | null; title: string; data: Record<string, unknown>; created_at: string }
export interface TemplateInput { id?: string; subject_id?: number | null; title?: string; data?: Record<string, unknown> }

export const getSemesters = (o: { search?: string; fetch: Fetch }): Promise<Semester[]> =>
  o.fetch(`/api/tasks/submit/semesters${o.search ? `?${new URLSearchParams({ search: o.search })}` : ''}`);
export const createSemester = (o: { semester: SemesterUpsert; fetch: Fetch }): Promise<{ ids: string[] }> => o.fetch('/api/tasks/submit/semesters', json('POST', o.semester));
export const updateSemester = (o: { id: string; patch: Omit<SemesterUpsert, 'id'>; fetch: Fetch }): Promise<{ id: string }> => o.fetch(`/api/tasks/submit/semesters/${encodeURIComponent(o.id)}`, json('PATCH', o.patch));
export const deleteSemester = (o: { id: string; fetch: Fetch }): Promise<null> => o.fetch(`/api/tasks/submit/semesters/${encodeURIComponent(o.id)}`, { method: 'DELETE' });

export function getSubjects(o: { search?: string; semesterId?: string | null; reviewerId?: string | null; fetch: Fetch }): Promise<Subject[]> {
  const p = new URLSearchParams();
  if (o.search) p.set('search', o.search);
  if (o.semesterId) p.set('semester_id', o.semesterId);
  if (o.reviewerId) p.set('reviewer_id', o.reviewerId);
  return o.fetch(`/api/tasks/submit/subjects${p.toString() ? `?${p}` : ''}`);
}
export const createSubject = (o: { subject: SubjectInput; fetch: Fetch }): Promise<{ id: number }> => o.fetch('/api/tasks/submit/subjects', json('POST', o.subject));
export const updateSubject = (o: { id: number; patch: SubjectInput; fetch: Fetch }): Promise<{ id: number }> => o.fetch(`/api/tasks/submit/subjects/${o.id}`, json('PATCH', o.patch));
export const deleteSubject = (o: { id: number; fetch: Fetch }): Promise<null> => o.fetch(`/api/tasks/submit/subjects/${o.id}`, { method: 'DELETE' });

export interface AwhParticipantInput { student_id?: string; student_name?: string }
export interface LectureEnsureReq { subject_id: number; number: number; operator?: AwhParticipantInput | null; editor?: AwhParticipantInput | null }
export interface LectureEnsureResult { lecture_id: number; created: boolean }

export const ensureLecture = (o: { req: LectureEnsureReq; fetch: Fetch }): Promise<LectureEnsureResult> => o.fetch('/api/tasks/submit/lectures/ensure', json('POST', o.req));
export interface Lecture {
  id: number; subject_id: number | null; awh_multiplier: number; operator_money_coef: number; editor_money_coef: number;
  review_state: ReviewState; number: number; title: string | null; author_id: string | null;
}
export interface LecturePatch { subject_id?: number | null; number?: number }

export const updateLecture = (o: { id: number; patch: LecturePatch; fetch: Fetch }): Promise<Lecture> => o.fetch(`/api/tasks/submit/lectures/${o.id}`, json('PATCH', o.patch));
/** Gives a task published without a lecture one: the subject's lecture with that number, created if missing. */
export async function setTaskLecture(o: { taskId: string; subjectId: number; number: number; fetch: Fetch }): Promise<UploadTask> {
  const data = await o.fetch(`/api/tasks/${encodeURIComponent(o.taskId)}/lecture`, json('PUT', { subject_id: o.subjectId, number: o.number }));
  return { ...data, created_at: new Date(data.created_at) };
}

export function getAwhSubmissions(o: { status?: AwhState; type?: 'id' | 'name'; limit?: number; start?: number; fetch: Fetch }): Promise<AwhSubmission[]> {
  const p = new URLSearchParams();
  if (o.status) p.set('status', o.status);
  if (o.type) p.set('type', o.type);
  if (o.limit) p.set('limit', String(o.limit));
  if (o.start) p.set('start', String(o.start));
  const qs = p.toString();
  return o.fetch(`/api/tasks/submit/awh_submissions${qs ? `?${qs}` : ''}`);
}
export const updateAwhSubmission = (o: { id: number; patch: { state?: AwhState; awh?: number; money_coef?: number; subject_id?: number | null; student_id?: string | null; student_name?: string | null }; fetch: Fetch }) => o.fetch(`/api/tasks/submit/awh_submissions/${o.id}`, json('PATCH', o.patch));
export interface AwhExportOperation { activist_id: number; name: string; hours: number; money_coef: number; student_id?: string | null }
export interface AwhExportPlannedTask { subject_id: number; name: string; description: string; operations: AwhExportOperation[]; submission_ids: number[] }
export interface AwhExportSkipped { submission_id: number; subject_title: string | null; lecture_number: number | null; role: AwhRole; who: string; awh: number; reason: string }
export interface AwhExportFlagged { submission_id: number; subject_title: string | null; lecture_number: number | null; role: AwhRole; who: string; awh: number; money_coef: number; created_by: string | null; reasons: string[] }
export interface AwhExportPreview { tasks: AwhExportPlannedTask[]; skipped: AwhExportSkipped[]; flagged: AwhExportFlagged[] }
export interface AwhExportTask { id: number; subject_id: number | null; name: string; mipt_task_id: number | null; state: 'success' | 'failed'; error: string | null; operations: AwhExportOperation[]; cancelled_at?: string | null; cancelled_by?: string | null }
export interface AwhExport { id: number; created_at: string; created_by: string; event_id: number; period_label: string; mipt_username: string | null; finished_at: string | null; tasks: AwhExportTask[] }

/** Which pending submissions an export takes: created in [createdFrom, createdBefore), ISO timestamps; either end may be left out. */
export interface AwhExportFilter { periodLabel: string; createdFrom?: string; createdBefore?: string }
/** The exporter's own mipt.tech login: sent with the request, never stored. */
export interface MiptTechCredentials { username: string; password: string }
const awhExportBody = (f: AwhExportFilter, c: MiptTechCredentials) => ({ period_label: f.periodLabel, ...(f.createdFrom && { created_from: f.createdFrom }), ...(f.createdBefore && { created_before: f.createdBefore }), mipt_username: c.username, mipt_password: c.password });
export const previewAwhExport = (o: { filter: AwhExportFilter; credentials: MiptTechCredentials; fetch: Fetch }): Promise<AwhExportPreview> => o.fetch('/api/tasks/submit/awh_exports/preview', json('POST', awhExportBody(o.filter, o.credentials)));
/** taskNames: names typed over the generated ones, by subject id. submissionIds: export only these (unset: all pending). */
export const createAwhExport = (o: { eventId: number; filter: AwhExportFilter; credentials: MiptTechCredentials; taskNames?: Record<number, string>; submissionIds?: number[]; fetch: Fetch }): Promise<AwhExport> => o.fetch('/api/tasks/submit/awh_exports', json('POST', { event_id: o.eventId, ...awhExportBody(o.filter, o.credentials), task_names: o.taskNames ?? {}, ...(o.submissionIds && { submission_ids: o.submissionIds }) }));
/** Deletes the task on mipt.tech and makes its submissions pending again. */
export const cancelAwhExportTask = (o: { taskId: number; credentials: MiptTechCredentials; fetch: Fetch }): Promise<AwhExportTask> => o.fetch(`/api/tasks/submit/awh_exports/tasks/${o.taskId}/cancel`, json('POST', { mipt_username: o.credentials.username, mipt_password: o.credentials.password }));
export const getAwhExports = (o: { fetch: Fetch }): Promise<AwhExport[]> => o.fetch('/api/tasks/submit/awh_exports');
export const createAwhSubmission = (o: { payload: AwhSubmissionCreate; fetch: Fetch }) => o.fetch('/api/tasks/submit/awh_submissions', json('POST', o.payload));
export const deleteAwhSubmission = (o: { id: number; fetch: Fetch }) => o.fetch(`/api/tasks/submit/awh_submissions/${o.id}`, { method: 'DELETE' });

export function getRegistrations(o: { status?: RegistrationStatus; limit?: number; start?: number; fetch: Fetch }): Promise<Registration[]> {
  const p = new URLSearchParams({ status: o.status ?? 'pending' });
  if (o.limit != null) p.set('limit', String(o.limit));
  if (o.start != null) p.set('start', String(o.start));
  return o.fetch(`/api/registrations?${p}`);
}
export const updateRegistration = (o: { id: string; patch: RegistrationUpdate; fetch: Fetch }) => o.fetch(`/api/registrations/${encodeURIComponent(o.id)}`, json('PATCH', o.patch));
export const approveRegistration = (o: { id: string; overwrite?: boolean; fetch: Fetch }) => o.fetch(`/api/registrations/${encodeURIComponent(o.id)}/approve${o.overwrite ? '?overwrite=true' : ''}`, { method: 'POST' });
export const declineRegistration = (o: { id: string; reason?: string; fetch: Fetch }) => o.fetch(`/api/registrations/${encodeURIComponent(o.id)}/decline`, json('POST', { reason: o.reason ?? null }));

export const getTemplates = (o: { fetch: Fetch }): Promise<Template[]> => o.fetch('/api/tasks/templates');
export const getTemplate = (o: { id: string; fetch: Fetch }): Promise<Template> => o.fetch(`/api/tasks/templates/${encodeURIComponent(o.id)}`);
export const saveTemplate = (o: { template: TemplateInput; fetch: Fetch }): Promise<Template> => o.fetch('/api/tasks/templates', json('POST', o.template));
export const deleteTemplate = (o: { id: string; fetch: Fetch }): Promise<null> => o.fetch(`/api/tasks/templates/${encodeURIComponent(o.id)}`, { method: 'DELETE' });
export const copyTemplate = (o: { id: string; fetch: Fetch }): Promise<Template> => o.fetch(`/api/tasks/templates/${encodeURIComponent(o.id)}`, { method: 'POST' });

export interface Student { phystech_email: string; last_name: string; first_name: string; middle_name: string | null; gmail: string | null; vk_id: string | null; mipt_tech_tag: string | null; mipt_tech_activist_id?: number | null; archived_at?: string | null }
export interface StudentSelfUpdate { gmail?: string | null; vk_id?: string | null }
export interface StudentUpdate { last_name?: string; first_name?: string; middle_name?: string | null; gmail?: string | null; vk_id?: string | null; mipt_tech_tag?: string | null; mipt_tech_activist_id?: number | null }
export interface StudentLookup { phystech_email: string; last_name: string; first_name: string; middle_name: string | null }

export function getStudentLookup(o: { search: string; limit?: number; fetch: Fetch }): Promise<StudentLookup[]> {
  const p = new URLSearchParams({ search: o.search });
  if (o.limit != null) p.set('limit', String(o.limit));
  return o.fetch(`/api/students/lookup?${p}`);
}
export const getStudentMe = (o: { fetch: Fetch }): Promise<Student> => o.fetch('/api/students/me');
export const updateStudentMe = (o: { patch: StudentSelfUpdate; fetch: Fetch }): Promise<Student> => o.fetch('/api/students/me', json('PATCH', o.patch));
export type StudentStatus = 'active' | 'archived' | 'all';
export function getStudents(o: { search?: string; limit?: number; start?: number; status?: StudentStatus; fetch: Fetch }): Promise<Student[]> {
  const p = new URLSearchParams();
  if (o.search) p.set('search', o.search);
  if (o.status) p.set('status', o.status);
  if (o.limit != null) p.set('limit', String(o.limit));
  if (o.start != null) p.set('start', String(o.start));
  return o.fetch(`/api/students${p.toString() ? `?${p}` : ''}`);
}
export const setStudentArchived = (o: { phystech_email: string; archived: boolean; fetch: Fetch }): Promise<Student> => o.fetch(`/api/students/${encodeURIComponent(o.phystech_email)}/${o.archived ? 'archive' : 'unarchive'}`, { method: 'POST' });
export const updateStudent = (o: { phystech_email: string; patch: StudentUpdate; fetch: Fetch }): Promise<Student> => o.fetch(`/api/students/${encodeURIComponent(o.phystech_email)}`, json('PATCH', o.patch));
